import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Users, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Плавный счётчик от 0 до target (ease-out)
const useCountUp = (target, active, duration = 2200) => {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    let startTime = null;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    const step = (ts) => {
      if (startTime === null) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.round(easeOut(progress) * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(step);
      }
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, active, duration]);

  return value;
};

const pluralCities = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "ГОРОД";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return "ГОРОДА";
  return "ГОРОДОВ";
};

const StatBlock = ({ Icon, value, label, theme }) => (
  <div className="flex flex-col gap-2" style={{ minWidth: 0 }}>
    <div className="flex items-center gap-2">
      <Icon size={16} style={{ color: theme.accent }} strokeWidth={2} />
      <span
        className="text-xs uppercase font-medium"
        style={{
          color: theme.label,
          letterSpacing: "0.2em",
          fontFamily: "IBM Plex Sans, sans-serif",
        }}
      >
        {label}
      </span>
    </div>
    <span
      className="text-5xl sm:text-6xl font-light leading-none"
      style={{
        color: theme.number,
        fontFamily: "Manrope, sans-serif",
        letterSpacing: "-0.03em",
      }}
    >
      {value.toLocaleString("ru-RU")}
    </span>
  </div>
);

/**
 * Блок социального доказательства: "Уже N исполнителей в M городах".
 * Появляется только когда админ включил блок и достигнут порог по исполнителям.
 * variant: "dark" (для тёмной navy-панели) | "light" (для светлых поверхностей)
 */
export const SocialProofCounter = ({ variant = "dark", className = "" }) => {
  const [stats, setStats] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/public/platform-stats`)
      .then((res) => { if (active) setStats(res.data); })
      .catch(() => { if (active) setStats(null); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (stats && stats.show) {
      const t = setTimeout(() => setVisible(true), 100);
      return () => clearTimeout(t);
    }
  }, [stats]);

  const executors = useCountUp(stats?.executors_count || 0, visible);
  const cities = useCountUp(stats?.cities_count || 0, visible);

  if (!stats || !stats.show) return null;

  const theme = variant === "dark"
    ? {
        accent: "var(--rv-teal)",
        label: "var(--rv-gold)",
        number: "var(--rv-paper)",
        context: "rgba(255,255,255,0.6)",
        border: "var(--rv-teal)",
      }
    : {
        accent: "var(--rv-teal)",
        label: "#b45309",
        number: "var(--rv-navy)",
        context: "var(--rv-text-muted)",
        border: "var(--rv-teal)",
      };

  return (
    <div
      data-testid="social-proof-section"
      className={`${className}`}
      style={{
        borderLeft: `2px solid ${theme.border}`,
        paddingLeft: "1.5rem",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
    >
      <p
        className="text-xs uppercase mb-4"
        style={{
          color: theme.context,
          letterSpacing: "0.18em",
          fontFamily: "IBM Plex Sans, sans-serif",
          fontWeight: 600,
        }}
      >
        Уже с нами
      </p>
      <div className="flex flex-row items-end gap-10 sm:gap-14">
        <span data-testid="executors-count-value" style={{ display: "contents" }}>
          <StatBlock Icon={Users} value={executors} label="ИСПОЛНИТЕЛЕЙ" theme={theme} />
        </span>
        <span data-testid="cities-count-value" style={{ display: "contents" }}>
          <StatBlock Icon={MapPin} value={cities} label={pluralCities(stats.cities_count || 0)} theme={theme} />
        </span>
      </div>
    </div>
  );
};

export default SocialProofCounter;
